import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, ArrowRight } from "lucide-react";
import { client } from "../lib/config";
import PageHero from "../components/PageHero";
import Breadcrumbs from "../components/Breadcrumbs";
import BookCallCTA from "../components/BookCallCTA";

export default function BlogListPage() {
  const [posts, setPosts] = useState([]);
  const [category, setCategory] = useState("all");

  useEffect(() => {
    client.get("/blog-posts").then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  const categories = ["all", ...Array.from(new Set(posts.map((p) => p.category)))];
  const visible = category === "all" ? posts : posts.filter((p) => p.category === category);

  return (
    <>
      <PageHero
        eyebrow="Journal"
        title="Local stories, honest guides, and the Clinton MO neighborhood."
        intro="Notes from the front desk. Truman Lake, the Katy Trail, deer season, Olde Glory Days, nearby towns, and the reasons people book us."
        image={null}
        metaTitle="Blog — Local Stories & Guides from Clinton MO"
        metaDesc="Blog from Westbridge Inn & Suites — local guides to Truman Lake, the Katy Trail, Clinton square, nearby Missouri towns, and hospitality notes from the front desk."
      />
      <Breadcrumbs items={[{ label: "Journal" }]} />

      <section className="bg-[#FDFBF7]">
        <div className="wb-container py-12 md:py-16">
          <div className="flex flex-wrap gap-2 mb-10" data-testid="blog-category-filters">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`blog-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`px-4 py-2 text-sm font-bold rounded-sm border ${
                  category === c ? "bg-[#E85D26] text-white border-[#E85D26]" : "bg-white border-[#E5DFD3] text-[#1A1A1A] hover:border-[#E85D26]"
                }`}
                style={{ fontFamily: "'Nunito', sans-serif" }}
              >
                {c === "all" ? "All" : c}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visible.map((p) => (
              <article key={p.slug} className="wb-card bg-white group flex flex-col hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-500" data-testid={`blog-card-${p.slug}`}>
                <Link to={`/blog/${p.slug}`} className="block">
                  {p.hero && !p.hero.includes("/area-") ? (
                    <div className="aspect-[4/3] overflow-hidden bg-[#F5F0E6]">
                      <img src={p.hero} alt="" aria-hidden="true" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    </div>
                  ) : (
                    <div className="aspect-[4/3] relative overflow-hidden bg-[#1A1A1A]">
                      <div className="absolute inset-0 bg-gradient-to-br from-[#B94A15]/70 via-[#1A1A1A] to-[#1A1A1A]" aria-hidden="true" />
                      <div aria-hidden="true" className="absolute inset-0 opacity-40 group-hover:opacity-55 transition-opacity duration-500" style={{ backgroundImage: "radial-gradient(60% 55% at 22% 30%, rgba(232,93,38,0.55) 0%, rgba(232,93,38,0) 60%), radial-gradient(50% 45% at 82% 78%, rgba(245,185,141,0.35) 0%, rgba(245,185,141,0) 60%)" }} />
                      <div className="relative h-full flex flex-col justify-end p-5">
                        <div className="text-[10px] font-black uppercase tracking-[0.22em] text-[#FF9666]" style={{ fontFamily: "'Nunito', sans-serif" }}>{p.category}</div>
                        <div className="mt-1 text-lg font-black text-white leading-tight" style={{ fontFamily: "'Nunito', sans-serif" }}>{p.title}</div>
                      </div>
                    </div>
                  )}
                </Link>
                <div className="p-6 flex flex-col flex-1">
                  <div className="wb-eyebrow">{p.category}</div>
                  <h3 className="mt-2 text-xl font-black text-[#1A1A1A]" style={{ fontFamily: "'Nunito', sans-serif" }}>
                    <Link to={`/blog/${p.slug}`} className="hover:text-[#B94A15]">{p.title}</Link>
                  </h3>
                  <p className="wb-body text-sm mt-2 flex-1">{p.excerpt}</p>
                  <div className="mt-4 pt-3 border-t border-[#E5DFD3] flex items-center justify-between text-xs text-[#4A4641]">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3 text-[#B94A15]" />
                      {new Date(p.published_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </span>
                    <Link to={`/blog/${p.slug}`} className="font-bold text-[#B94A15] hover:gap-2 flex items-center gap-1" style={{ fontFamily: "'Nunito', sans-serif" }}>
                      Read <ArrowRight className="h-3 w-3" />
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <BookCallCTA />
    </>
  );
}
